/* eslint-disable */
import { FuseNavigationItem } from '@fuse/components/navigation';
import { CustomerFeatureKey } from 'app/core/customer/customer-config.model';

import {
  BOL_SUMMARY_FEATURE,
  CONTROL_CENTER_FEATURE,
  EVENT_SUMMARY_FEATURE,
  GDST_LOCATIONS_FEATURE,
  GDST_TRADE_ITEMS_FEATURE,
  GDST_TRADING_PARTIES_FEATURE,
  GDST_VESSELS_FEATURE,
  LOT_SHARING_FEATURE,
  LOT_TRACING_FEATURE,
  MASTER_CERTIFICATION_DATA_FEATURE,
  MASTER_SPECIES_FEATURE,
  SETTINGS_CONFIG_FEATURE,
  SETTINGS_DESTINATION_SERVICES_FEATURE,
  SETTINGS_USERS_FEATURE,
  WILD_AGGREGATION_FEATURE,
  WILD_BUTCHERING_FEATURE,
  WILD_DISAGGREGATION_FEATURE,
  WILD_FISHING_FEATURE,
  WILD_FISHING_LANDING_FEATURE,
  WILD_COLD_STORAGE_FEATURE,
  WILD_LAND_BASE_RECEIPT_FEATURE,
  WILD_ON_VESSEL_PROCESSING_FEATURE,
  WILD_PROCESSING_FEATURE,
  WILD_RECEIVE_FEATURE,
  WILD_SHIPPING_FEATURE,
  WILD_TRANSFER_FEATURE,
  WILD_TRANSSHIPMENT_FEATURE,
  AUX_VEHICLE_FEATURE,
  AUX_CN_DATA_FEATURE,
} from '../customer/customer-features';

type CustomerNavigationItem = FuseNavigationItem & {
  meta?: {
    featureKey?: CustomerFeatureKey;
    roles?: string[];
  };
};

const QUALITY_ONLY = ['ROLE_QUALITY'];
const QUALITY_AND_DATA_ENTRY = ['ROLE_QUALITY', 'ROLE_DATA_ENTRY'];
const ADMIN_ONLY: string[] = [];

/** Reports */
const traceabilityReports: CustomerNavigationItem[] = [
  {
    id: 'event-summary',
    title: 'Event Summary',
    type: 'basic',
    link: '/tools/reports/event-summary',
    meta: { featureKey: EVENT_SUMMARY_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'trace',
    title: 'LOT Tracing',
    type: 'basic',
    link: '/tools/traceability/trace',
    meta: { featureKey: LOT_TRACING_FEATURE, roles: QUALITY_ONLY },
  },
  {
    id: 'lot-sharing',
    title: 'LOT Sharing',
    type: 'basic',
    link: '/tools/traceability/lot-sharing',
    meta: { featureKey: LOT_SHARING_FEATURE, roles: QUALITY_ONLY },
  },
  {
    id: 'bol-summary',
    title: 'BOL Summary',
    type: 'basic',
    link: '/tools/reports/bol-summary',
    meta: { featureKey: BOL_SUMMARY_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
];

/** Wild Harvest CTEs */
const criticalTrackingEvents: CustomerNavigationItem[] = [
  {
    id: 'fishing-landing',
    title: 'Fishing/Landing',
    type: 'basic',
    link: '/ctes/fishing-landing',
    meta: { featureKey: WILD_FISHING_LANDING_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'fishing',
    title: 'Fishing',
    type: 'basic',
    link: '/ctes/fishing',
    meta: { featureKey: WILD_FISHING_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'on-vessel-processing',
    title: 'On Vessel Processing',
    type: 'basic',
    link: '/ctes/on-vessel-processing',
    meta: { featureKey: WILD_ON_VESSEL_PROCESSING_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'transshipment',
    title: 'Transshipment',
    type: 'basic',
    link: '/ctes/transshipment',
    meta: { featureKey: WILD_TRANSSHIPMENT_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'land-base-receipt',
    title: 'Landing',
    type: 'basic',
    link: '/ctes/land-base-receipt',
    meta: { featureKey: WILD_LAND_BASE_RECEIPT_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'transfer',
    title: 'Transfer',
    type: 'basic',
    link: '/ctes/transferring',
    meta: { featureKey: WILD_TRANSFER_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'receipt',
    title: 'Receiving',
    type: 'basic',
    link: '/ctes/receive',
    meta: { featureKey: WILD_RECEIVE_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'butchering',
    title: 'Butchering',
    type: 'basic',
    link: '/ctes/butchering',
    meta: { featureKey: WILD_BUTCHERING_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'processing',
    title: 'Processing',
    type: 'basic',
    link: '/ctes/processing',
    meta: { featureKey: WILD_PROCESSING_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'cold-storage',
    title: 'Cold Storage',
    type: 'basic',
    link: '/ctes/cold-storage',
    meta: { featureKey: WILD_COLD_STORAGE_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'aggregation',
    title: 'Aggregation',
    type: 'basic',
    link: '/ctes/aggregation',
    meta: { featureKey: WILD_AGGREGATION_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'disaggregation',
    title: 'Disaggregation',
    type: 'basic',
    link: '/ctes/disaggregation',
    meta: { featureKey: WILD_DISAGGREGATION_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  {
    id: 'shipping',
    title: 'Shipping',
    type: 'basic',
    link: '/ctes/shipping',
    meta: { featureKey: WILD_SHIPPING_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
];

const auxiliaryData: CustomerNavigationItem[] = [
  {
    id: 'species',
    title: 'Species',
    type: 'basic',
    link: '/master/species',
    meta: { featureKey: MASTER_SPECIES_FEATURE, roles: ADMIN_ONLY },
  },
  {
    id: 'certification-data',
    title: 'Certification Data',
    type: 'basic',
    link: '/master/certification-data',
    meta: { featureKey: MASTER_CERTIFICATION_DATA_FEATURE, roles: ADMIN_ONLY },
  },
  {
    id: 'vehicle',
    title: 'Vehicles',
    type: 'basic',
    link: '/aux/vehicle',
    meta: { featureKey: AUX_VEHICLE_FEATURE, roles: ADMIN_ONLY },
  },
  {
    id: 'cn-data',
    title: 'CN Data',
    type: 'basic',
    link: '/aux/cn-data',
    meta: { featureKey: AUX_CN_DATA_FEATURE, roles: ADMIN_ONLY },
  },
];

const settingsData: CustomerNavigationItem[] = [
  {
    id: 'users',
    title: 'Users / Roles',
    type: 'basic',
    link: '/settings/users',
    meta: { featureKey: SETTINGS_USERS_FEATURE, roles: ADMIN_ONLY },
  },
  {
    id: 'destination-services',
    title: 'Destination Services',
    type: 'basic',
    link: '/settings/destination-services',
    meta: { featureKey: SETTINGS_DESTINATION_SERVICES_FEATURE, roles: ADMIN_ONLY },
  },
  {
    id: 'config',
    title: 'Configuration',
    type: 'basic',
    link: '/settings/config',
    meta: { featureKey: SETTINGS_CONFIG_FEATURE, roles: ADMIN_ONLY },
  },
];

const gdstMasterData: CustomerNavigationItem[] = [
  {
    id: 'trading-parties',
    title: 'Trading Parties',
    type: 'basic',
    link: '/gdst-tools/master-data/trading-parties',
    meta: { featureKey: GDST_TRADING_PARTIES_FEATURE, roles: QUALITY_ONLY },
  },
  {
    id: 'locations',
    title: 'Locations',
    type: 'basic',
    link: '/gdst-tools/master-data/locations',
    meta: { featureKey: GDST_LOCATIONS_FEATURE, roles: QUALITY_ONLY },
  },
  {
    id: 'vessels',
    title: 'Vessels',
    type: 'basic',
    link: '/gdst-tools/master-data/vessels',
    meta: { featureKey: GDST_VESSELS_FEATURE, roles: QUALITY_ONLY },
  },
  {
    id: 'trade-items',
    title: 'Trade Items',
    type: 'basic',
    link: '/gdst-tools/master-data/trade-items',
    meta: { featureKey: GDST_TRADE_ITEMS_FEATURE, roles: QUALITY_ONLY },
  },
];

export const defaultNavigation: CustomerNavigationItem[] = [
  {
    id: 'cte-control-center',
    title: 'Control Center',
    type: 'basic',
    icon: 'heroicons_outline:building-office',
    link: '/tools/reports/cte-control-center',
    meta: { featureKey: CONTROL_CENTER_FEATURE, roles: QUALITY_AND_DATA_ENTRY },
  },
  { id: 'divider-1', type: 'divider' },
  {
    id: 'traceability',
    title: 'Traceability Tools / Reports',
    type: 'collapsable',
    icon: 'heroicons_outline:document-chart-bar',
    children: traceabilityReports,
  },
  {
    id: 'cte',
    title: 'Critical Tracking Events (CTEs)',
    type: 'collapsable',
    icon: 'heroicons_outline:clipboard-document-list',
    children: criticalTrackingEvents,
  },
  /* NEW groups appended */
  { id: 'divider-extra-1', type: 'divider' },
  {
    id: 'catch-certificate',
    title: 'Catch Certificate',
    type: 'basic',
    icon: 'heroicons_outline:document-check',
    link: '/tools/catch-certificate',
    meta: { featureKey: 'wild.catch-certificate' },
  },
  {
    id: 'aux',
    title: 'Auxiliary Data',
    type: 'collapsable',
    icon: 'heroicons_outline:circle-stack',
    children: auxiliaryData,
  },
  {
    id: 'master-data',
    title: 'Master Data',
    type: 'collapsable',
    icon: 'heroicons_outline:queue-list',
    link: '/gdst-tools/master-data',
    children: gdstMasterData,
  },
  {
    id: 'settings',
    title: 'Settings',
    type: 'collapsable',
    icon: 'heroicons_outline:cog',
    children: settingsData,
  },
];

export const compactNavigation: CustomerNavigationItem[] = defaultNavigation;
export const futuristicNavigation: CustomerNavigationItem[] = defaultNavigation;
export const horizontalNavigation: CustomerNavigationItem[] = defaultNavigation;
